import { Checkbox, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import { NETWORKS, PROCESSORS, type OnboardingAnswers } from "../../types/onboarding";
import { NETWORK_LABELS, PROCESSOR_LABELS, toOtherProcessorNames } from "./labels";
import { toggle } from "./toggle";

export function SwitchCard({ title, hint, checked, onChange, children }: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{hint}</Typography>
        </div>
        <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} slotProps={{ input: { role: "switch", "aria-label": title } }} />
      </div>
      {checked && children}
    </div>
  );
}

export default function ServicesSection({ answers, set }: {
  answers: OnboardingAnswers;
  set: (patch: Partial<OnboardingAnswers>) => void;
}) {
  // Representment can cover only some of the client's processors; staff tick the ones it does.
  const processors = PROCESSORS.filter((p) => p !== "other" && answers.brands.some((b) => b.processors.includes(p)));
  const otherNames = toOtherProcessorNames(answers.brands);
  const covered = answers.representmentProcessors ?? [];
  const coveredNames = answers.representmentOtherNames ?? [];
  const ticks = [
    ...processors.map((p) => ({ key: p, label: PROCESSOR_LABELS[p], checked: covered.includes(p), onToggle: () => set({ representmentProcessors: toggle(covered, p) }) })),
    ...otherNames.map((name) => ({ key: `other:${name}`, label: name, checked: coveredNames.includes(name), onToggle: () => set({ representmentOtherNames: toggle(coveredNames, name) }) })),
  ];

  return (
    <>
      <SwitchCard
        title="Alerts" hint="Pre-chargeback alerts. Adds enrolments, refunds and go-live steps."
        checked={answers.alerts.enabled} onChange={(enabled) => set({ alerts: { ...answers.alerts, enabled } })}
      >
        <div className="flex flex-wrap gap-6">
          {NETWORKS.map((n) => (
            <FormControlLabel
              key={n} label={NETWORK_LABELS[n]}
              control={<Checkbox checked={answers.alerts.networks.includes(n)} onChange={() => set({ alerts: { ...answers.alerts, networks: toggle(answers.alerts.networks, n) } })} />}
            />
          ))}
        </div>
      </SwitchCard>
      <SwitchCard
        title="Chargeback representment" hint="We fight chargebacks with evidence."
        checked={answers.representment} onChange={(representment) => set({ representment })}
      >
        {ticks.length > 0 && (
          <div className="flex flex-wrap gap-6">
            {ticks.map(({ key, label, checked, onToggle }) => (
              <FormControlLabel
                key={key} label={label}
                control={<Checkbox checked={checked} onChange={onToggle} slotProps={{ input: { "aria-label": `Representment covers ${label}` } }} />}
              />
            ))}
          </div>
        )}
      </SwitchCard>
      <TextField
        label="Add-ons and agreed service levels" value={answers.addOns} multiline minRows={3} fullWidth
        onChange={(e) => set({ addOns: e.target.value })}
      />
    </>
  );
}
